import {
  CallIcon,
  EarthFreeIcons,
  ImageUpload01Icon,
  Mail01Icon,
  PassportIcon,
  UserIdVerificationIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import React, { useEffect, useState } from "react";
import { Stack } from "react-bootstrap";
import { Card } from "../../../CommonElements/Hajj/Card";
import { useLocalizedText } from "../../../utils/hooks/useLocalizedText";
import { useTranslation } from "react-i18next";
import { useUploadProfileImageMutation } from "../../../services/user";
import { FileUpload } from "../../../CommonElements/FileUpload";
import { getAvatarLetters } from "../../../utils/helpers";
import { useAuthContext } from "../../../_helper/Auth";
import { useParams } from "react-router";
import { LabelValue } from "../../../CommonElements/LableValue/LabelValue";

// Skeleton component for BasicInfo
export const BasicInfoSkeleton = () => {
  return (
    <Card
      header={
        <>
          <Stack direction="horizontal" gap={2}>
            <div className="avatar avatar-lg">
              <div className="skeleton skeleton-avatar"></div>
            </div>
            <Stack className="align-self-center" gap={1}>
              <div className="skeleton skeleton-name"></div>
              <div className="skeleton skeleton-name-secondary"></div>
            </Stack>
          </Stack>
          <div className="skeleton skeleton-profile-button"></div>
        </>
      }
    >
      <Stack gap={2}>
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="d-flex align-items-center justify-content-between"
          >
            <div className="d-flex align-items-center gap-2">
              <div className="skeleton skeleton-profile-icon"></div>
              <div className="skeleton skeleton-profile-label"></div>
            </div>
            <div className="skeleton skeleton-profile-value"></div>
            {index % 2 === 0 && (
              <div className="skeleton skeleton-copy-button"></div>
            )}
          </div>
        ))}
      </Stack>
    </Card>
  );
};

export const BasicInfo = (props) => {
  const { data } = props;
  const { id: routeId } = useParams();
  const {
    name_ar,
    name_en,
    email,
    phone,
    national_id,
    nationality,
    profile_image_url,
    gender,
  } = data;
  const [img, setImg] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { getLocalizedText } = useLocalizedText();
  const { t } = useTranslation();
  const name = getLocalizedText(name_ar, name_en);
  const { user } = useAuthContext();

  const id = routeId ?? user?.id;

  const { mutate: uploadProfileImage, isPending } =
    useUploadProfileImageMutation();

  // Clean up object URL on unmount and when preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (img) {
      // Create preview URL for the selected image
      const file = img.originFileObj || img;
      let currentPreviewUrl = null;
      if (file) {
        currentPreviewUrl = URL.createObjectURL(file);
        setPreviewUrl(currentPreviewUrl);
      }

      uploadProfileImage(
        { user_id: id, profile_image: img },
        {
          onSuccess: () => {
            setImg(null);
            // Clean up preview URL after successful upload
            if (currentPreviewUrl) {
              URL.revokeObjectURL(currentPreviewUrl);
              setPreviewUrl(null);
            }
          },
          onError: () => {
            setImg(null);
            // Clean up preview URL on error
            if (currentPreviewUrl) {
              URL.revokeObjectURL(currentPreviewUrl);
              setPreviewUrl(null);
            }
          },
          onSettled: () => {
            setImg(null);
          },
        }
      );
    }
  }, [img, id, uploadProfileImage]);

  const handleFileSelect = (file) => {
    // Clean up previous preview URL if exists
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    // Set the selected file, which will trigger the upload in useEffect
    setImg(file || null);
  };

  // Determine which image to show
  const displayImageUrl = previewUrl || profile_image_url;

  return (
    <Card
      header={
        <>
          <Stack direction="horizontal" gap={2}>
            <div className="avatar avatar-lg">
              {displayImageUrl ? (
                <img className="border-3 border" src={displayImageUrl} alt="" />
              ) : (
                <span>{getAvatarLetters(name)}</span>
              )}
            </div>
            <Stack className="align-self-center" gap={1}>
              <p className="H5 mb-0">{name_ar}</p>
              <p className="mb-0">{name_en}</p>
            </Stack>
          </Stack>
          <FileUpload
            onFileSelect={handleFileSelect}
            accept="image/*"
            maxSizeInMB={5}
            maxCount={1}
            loading={isPending}
            buttonText="CHANGE_PROFILE_PICTURE"
            loadingText="UPLOADING"
            icon={ImageUpload01Icon}
            buttonProps={{
              variant: "primary",
              className: "btn-xs btn-outline align-self-start",
            }}
          />
        </>
      }
    >
      <Stack gap={2}>
        <LabelValue
          label={t("NATIONAL_ID_LABEL")}
          value={national_id}
          icon={<HugeiconsIcon icon={PassportIcon} />}
          copyValue={national_id}
        />

        <LabelValue
          label={t("NATIONALITY_LABEL")}
          value={nationality}
          icon={<HugeiconsIcon icon={EarthFreeIcons} />}
        />

        <LabelValue
          label={t("GENDER_LABEL")}
          value={t(gender?.toUpperCase() ?? "")}
          icon={<HugeiconsIcon icon={UserIdVerificationIcon} />}
        />

        <LabelValue
          label={t("MOBILE_NUMBER_LABEL")}
          value={<span dir="ltr">{phone}</span>}
          icon={<HugeiconsIcon icon={CallIcon} />}
          copyValue={phone}
        />

        <LabelValue
          label={t("EMAIL_LABEL")}
          value={email}
          icon={<HugeiconsIcon icon={Mail01Icon} />}
          copyValue={email}
        />
      </Stack>
    </Card>
  );
};
